import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientTranslation } from '@/lib/core/i18n';
import { useCartStore, useLocaleStore } from '@/lib/core/stores';
import { useApp } from '@/lib/core/providers/appProvider';
import { LinkRouteUrl } from '@/lib/core/utils';
import type { AppContext } from '@/lib/core/types';
import { getPage } from '@/services/platform/data/cms';
import {
  getShopBagHref,
  getShopCheckoutHref,
  getShopOrderConfirmedHref,
  useShoppingBagContent,
} from '@/services/platform/data/shop';
import { getAddresses } from '@/services/platform/data/addresses';
import {
  CheckoutContexts,
  CheckoutPaymentStatuses,
  getPayment,
  isPaymentStatus,
  isTerminalPaymentStatus,
} from '@/services/platform/data/checkout';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { CheckoutScreen } from '@/components/shared/checkout/CheckoutScreen';
import type { CheckoutOutcome } from '@/components/shared/checkout/CheckoutForm';
import type { CheckoutPurchase } from '@/components/shared/checkout/types';
import type {
  AddressBookDto,
  CheckoutContentResponseModel,
  PickupPointContentResponseModel,
} from '@/services/platform/api/model';
import { OrderSummaryAccordionContent } from './OrderSummaryAccordionContent';
import { forgetShopCheckout, recallShopCheckout, rememberShopCheckout } from './session';

/** Navigation state consumed by the order-confirmed screen. */
export interface OrderConfirmationState {
  /**
   * The closed order's public guid, taken from the payment the customer just completed. The server
   * only reports `Succeeded` once the order exists, so this is exact — the confirmation screen does
   * not have to re-read the orders list and assume the newest one is theirs.
   */
  orderId?: string;
  /** Order number from the same payment, so the screen has something to show before its own fetch. */
  orderNumber?: string;
  /** ISO timestamp of the confirmation, used as the displayed order date fallback. */
  confirmedAt: string;
}

/** Navigation state sent back to the Bag when the payment is blocked by a CPI re-price. */
export interface BagPriceChangeState {
  priceChangeNotice: true;
  /** Unit price per variantId as it stood right before the payment attempt. */
  previousUnitPrices: Record<string, number | null | undefined>;
}

/**
 * The `shop` adapter over the generic `CheckoutScreen` (Figma: Checkout | Payment methods).
 *
 * The third payment context, and the odd one out: the purchase already exists before the money. A
 * cart is built over days, costs nothing to leave lying around and holds nothing scarce, so unlike a
 * seat or a room it needs no hold — `contextReference` is simply the cart's `publicGuid` and the
 * server re-prices it against CPI at intent time.
 *
 * That also means this adapter carries no router state and no booking copy: the cart is server-side,
 * so a refresh re-reads it. Only the MB WAY redirect return needs a breadcrumb, because a *paid*
 * cart no longer exists to be re-read — see `session.ts`.
 *
 * Reached at `/shop/checkout` (`PageMode.ShopCheckout`), which `Screen.tsx` renders with no props
 * and no CMS fetch, hence the `getPage` below for the shared `checkout` document type.
 */
export function ShopCheckoutScreen() {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const locale = useLocaleStore(state => state.locale);
  const { appSettings } = useApp();
  const { properties: bagCms, pickupPoints } = useShoppingBagContent();

  const publicGuid = useCartStore(state => state.publicGuid);
  const items = useCartStore(state => state.items);
  const totalAmount = useCartStore(state => state.totalAmount);
  const shipping = useCartStore(state => state.shipping);
  const discount = useCartStore(state => state.discount);
  const currencyCode = useCartStore(state => state.currencyCode);
  const giftMessage = useCartStore(state => state.giftMessage);
  const delivery = useCartStore(state => state.delivery);
  const hydrate = useCartStore(state => state.hydrate);
  const setCartFromDto = useCartStore(state => state.setCartFromDto);

  const [content, setContent] = useState<CheckoutContentResponseModel>();
  const [addresses, setAddresses] = useState<AddressBookDto[]>([]);
  /** False until the cart guid is known and any payment left in flight has been accounted for. */
  const [resolved, setResolved] = useState(false);

  /*
   * Every exit from this screen, and the last resort behind it.
   *
   * Unlike the bag and delivery steps, this screen cannot answer a missing mapping by refusing to
   * move: it is reached with a live cart and, past `handleSucceeded`, with the money already taken.
   * So it needs a destination that is always resolvable. `'/'` is that destination — the router's
   * index route, dependent on no mapping at all — and it is what the generic `CheckoutScreen`
   * already falls back to (`CheckoutScreen.tsx:153,163`), so this reuses that convention rather
   * than reintroducing a literal shop path.
   */
  const safeExit = getShopBagHref(appSettings) ?? '/';

  /**
   * The prices the customer was looking at when they pressed pay, captured before any re-price can
   * overwrite them. The bag renders these struck through next to the new ones, so "some prices
   * changed" is evidence rather than an assertion — which means they have to be read before the
   * payment, not after.
   */
  const previousUnitPricesRef = useRef<Record<string, number | null | undefined>>({});
  useEffect(() => {
    const prices: Record<string, number | null | undefined> = {};
    for (const item of items) {
      if (item.variantId) prices[item.variantId] = item.unitPrice;
    }
    previousUnitPricesRef.current = prices;
  }, [items]);

  const goToConfirmation = useCallback(
    (orderId?: string | null, orderNumber?: string | null) => {
      forgetShopCheckout();
      const state: OrderConfirmationState = {
        orderId: orderId ?? undefined,
        orderNumber: orderNumber ?? undefined,
        confirmedAt: new Date().toISOString(),
      };
      /*
       * The one destination in this flow that must never refuse to navigate. Everywhere else a
       * missing page mapping stops the customer before they pay; here the money has already been
       * taken, so leaving them on the payment screen would be the worst of the options. Degrades
       * through `safeExit`, and says so in the console — a paid order the customer cannot see is
       * worth a line even though the app recovers.
       */
      const confirmedHref = getShopOrderConfirmedHref(appSettings);
      if (!confirmedHref) {
        console.warn(
          `ShopCheckoutScreen: no shopOrderConfirmed route configured; sending the paid customer to ${safeExit}.`
        );
      }
      navigate(confirmedHref ?? safeExit, { replace: true, state });
    },
    [appSettings, navigate, safeExit]
  );

  // ── The CMS `checkout` page, shared with the event and Mews flows ──────────
  // Fetched here because `Screen.tsx` skips content resolution for the shop checkout modes. A miss
  // is not fatal: `CheckoutScreen` falls back to i18n labels and `FALLBACK_LOGOS`, and a missing CMS
  // page is no reason to stop the customer paying.
  useEffect(() => {
    const checkoutPath = LinkRouteUrl(appSettings?.properties?.checkout?.route);
    if (!checkoutPath) return;

    let isStale = false;
    getPage<CheckoutContentResponseModel>(checkoutPath, locale ?? undefined, false)
      .then(page => {
        if (!isStale) setContent(page);
      })
      .catch(error => console.warn('ShopCheckoutScreen: could not load the checkout page:', error));

    return () => {
      isStale = true;
    };
  }, [appSettings, locale]);

  // ── Resolve the cart, and recover a payment that outlived the page ─────────
  /*
   * Gated on `resolved` state rather than a `useRef` one-shot flag. A ref survives the
   * mount/unmount/remount that Strict Mode performs in development — the same trap documented on
   * `CheckoutForm`'s `isActiveRef` — so a ref set on the first setup makes the second setup skip and
   * the screen spins forever in every dev session. State is re-read on each setup, so the retry
   * happens; `isStale` is what stops the abandoned first pass from navigating.
   *
   * It also stops the resolution from re-running once the purchase is on screen: `handleSucceeded`
   * empties the cart on the way to the confirmation, and a second pass would read that empty cart
   * and race a redirect to the bag against it.
   */
  useEffect(() => {
    if (resolved) return;
    let isStale = false;

    (async () => {
      // The store is populated by the delivery step on the way here, but a refresh or a
      // `return_url` round trip arrives with `publicGuid` dropped (it is deliberately not persisted).
      let cartGuid = useCartStore.getState().publicGuid;
      if (!cartGuid) {
        await hydrate();
        cartGuid = useCartStore.getState().publicGuid;
      }
      if (isStale) return;

      const remembered = recallShopCheckout();

      /*
       * No cart, but a payment we started. Either it is still going (the customer is in their
       * banking app) or it already succeeded — in which case the cart is gone precisely *because* it
       * became the order, and bouncing to the bag would hide a purchase they just paid for. So ask
       * the payment rather than inferring anything from the empty cart.
       */
      if (!cartGuid && remembered?.paymentId) {
        const payment = await getPayment(remembered.paymentId);
        if (isStale) return;

        if (isPaymentStatus(payment?.status, CheckoutPaymentStatuses.Succeeded)) {
          goToConfirmation(payment?.orderId, payment?.orderNumber);
          return;
        }
        if (payment && !isTerminalPaymentStatus(payment.status)) {
          // Money in flight against a basket that is no longer readable. Nothing useful can be
          // rendered — the screen needs a purchase — but this must be visible in the console, because
          // it is the one shape where the customer may be charged for something they cannot see.
          console.warn(
            `ShopCheckoutScreen: payment ${remembered.paymentId} is ${payment.status} but its cart is gone.`
          );
        }
        forgetShopCheckout();
        navigate(safeExit, { replace: true });
        return;
      }

      if (!cartGuid || useCartStore.getState().items.length === 0) {
        // Deep link with an empty bag, or a cart emptied elsewhere — nothing to pay for.
        forgetShopCheckout();
        navigate(safeExit, { replace: true });
        return;
      }

      // A session left over from a different basket must not be allowed to claim this one's payment.
      const sameCart = remembered?.cartGuid === cartGuid;
      rememberShopCheckout({ cartGuid, paymentId: sameCart ? remembered?.paymentId : undefined });

      setResolved(true);
    })();

    return () => {
      isStale = true;
    };
  }, [safeExit, goToConfirmation, hydrate, navigate, resolved]);

  // The DELIVER TO row needs the address book, exactly as the delivery step resolves it.
  useEffect(() => {
    if (!delivery?.addressId) return;
    getAddresses(locale ?? undefined).then(data => setAddresses(data ?? []));
  }, [delivery?.addressId, locale]);

  const deliverTo = useMemo(() => {
    const address = delivery?.addressId
      ? addresses.find(candidate => candidate.id === delivery.addressId)
      : undefined;
    const pickupPoint = delivery?.pickupPointId
      ? pickupPoints.find((point: PickupPointContentResponseModel) => point.id === delivery.pickupPointId)
      : undefined;
    if (!address && !pickupPoint) return undefined;

    return {
      label: address?.label ?? pickupPoint?.properties?.displayName ?? pickupPoint?.name ?? '',
      detail: address
        ? [
            [address.firstName, address.lastName].filter(Boolean).join(' '),
            address.street,
            address.postalCode,
            [address.city, address.country].filter(Boolean).join(' - '),
          ]
            .filter(Boolean)
            .join(', ')
        : [
            pickupPoint?.properties?.addressLine,
            pickupPoint?.properties?.postalCode,
            [pickupPoint?.properties?.city, pickupPoint?.properties?.country].filter(Boolean).join(' - '),
          ]
            .filter(Boolean)
            .join(', '),
    };
  }, [addresses, delivery, pickupPoints]);

  const purchase: CheckoutPurchase | undefined = useMemo(() => {
    if (!resolved || !publicGuid) return undefined;

    // Same construction the bag uses to reach the delivery step (`ShopBagScreen.goContinue`). With
    // the mapping unset this screen is unreachable anyway — `getPageMode` cannot classify the path
    // without it — so the back destination degrades to the bag rather than to a literal URL.
    const checkoutHref = getShopCheckoutHref(appSettings);
    const subtotal = items.reduce((sum, item) => sum + (item.totalPrice ?? 0), 0);

    return {
      context: CheckoutContexts.Shop,
      contextReference: publicGuid,
      // Shown to the customer only. The server re-resolves the total from CPI and refuses the
      // payment with a 409 if the two disagree, so this is never what is charged.
      amountMajor: totalAmount,
      currencyCode,
      // Back goes to the delivery step, the screen this was reached from.
      returnPath: checkoutHref ? `${checkoutHref}/delivery` : safeExit,
      summaryTitle: bagCms?.viewOrderSummary || t('App.Shop.ViewOrderSummary'),
      summaryContent: (
        <OrderSummaryAccordionContent
          items={items}
          subtotal={subtotal}
          shipping={shipping}
          discount={discount}
          total={totalAmount}
          currencyCode={currencyCode}
          locale={locale ?? 'en-GB'}
          giftMessage={giftMessage}
          deliverTo={deliverTo}
        />
      ),
    };
  }, [
    appSettings,
    bagCms?.viewOrderSummary,
    safeExit,
    currencyCode,
    deliverTo,
    discount,
    giftMessage,
    items,
    locale,
    publicGuid,
    resolved,
    shipping,
    t,
    totalAmount,
  ]);

  const handleIntentCreated = useCallback(
    (paymentId: string) => {
      if (publicGuid) rememberShopCheckout({ cartGuid: publicGuid, paymentId });
    },
    [publicGuid]
  );

  const handleSucceeded = useCallback(
    (outcome: Extract<CheckoutOutcome, { kind: 'succeeded' }>) => {
      /*
       * Re-read the cart; never clear it. The cart *becomes* the order server-side, so there is
       * nothing left to tidy up — and `DELETE /me/cart` here is the one thing the API explicitly
       * warns against, because a webhook that finds an emptied basket refunds the customer instead
       * of delivering. `hydrate` picks up the fresh, empty cart the server now returns.
       *
       * Not awaited. The confirmation does not read the cart — only the bag badge does — and holding
       * a paid customer on the processing overlay until a refresh returns is worse than a badge that
       * updates a moment late: past 20s the overlay offers an escape hatch that would drop them back
       * on the delivery step, having already been charged.
       */
      void hydrate().catch(() => {
        // Reconciled by the next hydrate; nothing here is worth surfacing to a paying customer.
      });
      goToConfirmation(outcome.orderId, outcome.orderNumber);
    },
    [goToConfirmation, hydrate]
  );

  const handleOutcome = useCallback(
    (outcome: Exclude<CheckoutOutcome, { kind: 'succeeded' }>): boolean => {
      if (outcome.kind === 'stale') {
        // CPI re-priced the basket. Show the customer what changed rather than a bare "the price
        // changed" error: the refreshed cart arrives on the 409 body, and the bag draws its new
        // totals against the old prices.
        //
        // Read out of the ref *before* the cart is replaced. The ref is only rewritten by an effect,
        // so batching would keep the old values here anyway — but the correctness of this branch
        // should not rest on when React chooses to flush.
        const state: BagPriceChangeState = {
          priceChangeNotice: true,
          previousUnitPrices: previousUnitPricesRef.current,
        };
        if (outcome.repricedCart) setCartFromDto(outcome.repricedCart);
        forgetShopCheckout();
        navigate(safeExit, { replace: true, state });
        return true;
      }

      if (outcome.kind === 'alreadyPaid') {
        // Money was already taken for this basket. Offering another card would risk charging twice;
        // the order is what they are looking for. `orderId` is unknown here — the confirmation
        // screen resolves it.
        void hydrate();
        goToConfirmation();
        return true;
      }

      // Declines, timeouts and refunds mean the same thing here as anywhere else — let the generic
      // screen render its phases.
      return false;
    },
    [safeExit, goToConfirmation, hydrate, navigate, setCartFromDto]
  );

  if (!purchase) return <SpinLoading wrapperClassName="min-h-[60vh]" />;

  // Built whether or not the CMS page resolved: the only thing `CheckoutScreen` reads off `settings`
  // is the terms-and-conditions link, which lives in `appSettings` and is required on a screen that
  // takes money. Tying it to the checkout page's own lookup would drop the link on a CMS miss.
  const context: AppContext = { page: content, settings: appSettings };

  return (
    <div className="pt-safe-area relative flex h-full flex-col bg-white">
      <CheckoutScreen
        content={content}
        context={context}
        purchase={purchase}
        onSucceeded={handleSucceeded}
        onOutcome={handleOutcome}
        onIntentCreated={handleIntentCreated}
      />
    </div>
  );
}
